<!-- components/CategoryProductEntry.vue -->
<template>
  <section class="entry">
    <div v-if="entryLogoUrl" class="entry-logo-wrap">
      <a class="entry-logo-link" :href="overviewLinkHref" aria-label="Zur Startseite" @click.prevent="requestLeaveEditor(overviewLinkHref)">
        <img
          class="entry-logo"
          :src="entryLogoUrl"
          alt="PixelFotoExpress"
          loading="lazy"
          decoding="async"
        />
      </a>
    </div>

    <header class="topbar">
      <div class="crumbs" v-if="breadcrumbItems.length">
        <template v-for="(item, idx) in breadcrumbItems" :key="`${item.label}-${idx}`">
          <button
            v-if="item.action"
            class="crumb-btn"
            type="button"
            @click="item.action()"
          >{{ item.label }}</button>
          <span v-else class="crumb-current">{{ item.label }}</span>
          <span v-if="idx < breadcrumbItems.length - 1" class="sep">/</span>
        </template>
      </div>

      <div class="hero">
        <h1 class="headline">{{ headline }}</h1>
        <button
          class="btn btn-editor-link"
          type="button"
          @click="openMultiEditor"
        >
          Zum Editor
        </button>
      </div>
    </header>

    <!-- PARENTS -->
    <div v-if="step === 'parents'" class="panel">


      <div v-if="parentsLoading" class="grid skel">
        <div v-for="i in 10" :key="i" class="sk-card">
          <div class="sk-img"></div>
          <div class="sk-line l1"></div>
          <div class="sk-line l2"></div>
        </div>
      </div>

      <div v-else-if="parentsError" class="error">
        {{ parentsError.message }}
      </div>

      <div v-else class="stack">
        <section v-if="standardParentCategories.length" class="section-block">
          <div class="grid">
            <button v-for="c in standardParentCategories" :key="c.id" class="card card--category" type="button" @click="selectParent(c)">
              <img class="thumb" :src="c.image" :alt="c.title" loading="lazy" decoding="async" />
              <div class="meta">
                <div class="name">{{ c.title }}</div>
                <div class="entry-sub muted" v-if="c.productCount != null">{{ c.productCount }} Produkte</div>
              </div>
            </button>
          </div>
        </section>

        <section v-if="designerCategories.length" class="section-block">
          <h2 class="section-title">Produktdesigner</h2>
          <div class="grid">
            <button v-for="c in designerCategories" :key="c.id" class="card card--category" type="button" @click="selectParent(c)">
              <img class="thumb" :src="c.image" :alt="c.title" loading="lazy" decoding="async" />
              <div class="meta">
                <div class="name">{{ c.title }}</div>
              </div>
            </button>
          </div>
        </section>

        <div v-if="parentCategories.length === 0" class="empty muted">Keine Kategorien gefunden.</div>
      </div>
    </div>

    <!-- CHILDREN -->
    <div v-else-if="step === 'children'" class="panel">
      <div class="panel-head">
        <button class="btn ghost" type="button" @click="goToParents" :disabled="isBusy">← Zurück</button>

      </div>

      <div v-if="childrenLoading" class="grid skel">
        <div v-for="i in 12" :key="i" class="sk-card">
          <div class="sk-img"></div>
          <div class="sk-line l1"></div>
          <div class="sk-line l2"></div>
        </div>
      </div>

      <div v-else-if="childrenError" class="error">
        {{ childrenError.message }}
      </div>

      <div v-else class="grid">
        <button v-for="c in childCategories" :key="c.id" class="card card--category" type="button" @click="selectChild(c)">
          <img class="thumb" :src="c.image" :alt="c.title" loading="lazy" decoding="async" />
          <div class="meta">
            <div class="name">{{ c.title }}</div>
            <div class="entry-sub muted" v-if="c.productCount != null">{{ c.productCount }} Produkte</div>
          </div>
        </button>

        <div v-if="childCategories.length === 0" class="empty muted">Keine Unterkategorien gefunden.</div>
      </div>
    </div>

    <!-- PRODUCTS -->
    <div v-else class="panel">
      <div class="panel-head">
        <button class="btn ghost" type="button" @click="goToChildren" :disabled="isBusy">← Zurück</button>

      </div>

      <div v-if="prodLoading" class="grid skel">
        <div v-for="i in 12" :key="i" class="sk-card">
          <div class="sk-img"></div>
          <div class="sk-line l1"></div>
          <div class="sk-line l2"></div>
        </div>
      </div>

      <div v-else-if="prodError" class="error">
        {{ prodError.message }}
      </div>

      <div v-else class="grid">
        <button v-for="p in products" :key="p.id" class="card" type="button" @click="selectProduct(p)">
          <img class="thumb" :src="p.image" :alt="p.title" loading="lazy" decoding="async" />
          <div class="meta">
            <div class="name">{{ p.title }}</div>
            <div class="entry-sub muted" v-if="p.shortDescription">{{ p.shortDescription }}</div>
          </div>
        </button>

        <div v-if="products.length === 0" class="empty muted">Keine Produkte gefunden.</div>
      </div>
    </div>
  </section>

  <Teleport to="body">
    <div v-if="showUploadPrompt" class="uprompt-backdrop" @click.self="closeUploadPrompt">
      <div class="uprompt-panel" role="dialog" aria-modal="true" aria-label="Fotos hochladen">
        <template v-if="designerOffered">
          <div class="uprompt-title">Wie möchten Sie gestalten?</div>
          <div class="uprompt-modes" role="group" aria-label="Editor wählen">
            <button class="uprompt-mode" data-on="1" type="button" aria-pressed="true">
              <span class="uprompt-mode__icon"><ImageIcon :size="18" /></span>
              <span class="uprompt-mode__copy">
                <span class="uprompt-mode__title">Foto drucken</span>
                <span class="uprompt-mode__sub">Bild hochladen, zuschneiden, fertig</span>
              </span>
            </button>
            <button class="uprompt-mode" data-on="0" type="button" aria-pressed="false" :disabled="designerOpening" @click="openDesignerFromPrompt">
              <span class="uprompt-mode__icon uprompt-mode__icon--design"><Sparkles :size="18" /></span>
              <span class="uprompt-mode__copy">
                <span class="uprompt-mode__title">Individuell gestalten</span>
                <span class="uprompt-mode__sub">Vorlagen, Text, Collage &amp; Sticker</span>
              </span>
              <ArrowRight class="uprompt-mode__go" :size="16" />
            </button>
          </div>
          <div class="uprompt-text">Fotos hochladen – vom Computer oder Smartphone:</div>
        </template>
        <template v-else>
          <div class="uprompt-title">Fotos für diese Kategorie hinzufügen</div>
          <div class="uprompt-text">Wählen Sie Desktop oder Smartphone. Der Upload wird direkt dem aktuellen Projekt zugeordnet.</div>
        </template>
        <div class="uprompt-switch" aria-label="Upload-Quelle">
          <button class="uprompt-source" :data-on="uploadPromptSource === 'desktop' ? '1' : '0'" type="button" @click="uploadPromptSource = 'desktop'">
            <Monitor :size="16" />
            Desktop
          </button>
          <button class="uprompt-source" :data-on="uploadPromptSource === 'phone' ? '1' : '0'" type="button" @click="uploadPromptSource = 'phone'; buildPhoneBridge()">
            <Smartphone :size="16" />
            Fotos vom Smartphone
          </button>
        </div>
        <div
          v-if="uploadPromptSource === 'desktop'"
          class="uprompt-dropzone"
          :class="{ 'uprompt-dropzone--drag': isDragging }"
          role="button"
          tabindex="0"
          @click="confirmEmptyCategoryUploadPrompt"
          @keydown.enter.prevent="confirmEmptyCategoryUploadPrompt"
          @keydown.space.prevent="confirmEmptyCategoryUploadPrompt"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="(e: DragEvent) => { isDragging = false; if (e.dataTransfer?.files?.length) entryUploadRef?.handleFiles(e.dataTransfer.files) }"
        >
          <div class="uprompt-dropzone__icon"><Upload :size="28" /></div>
          <div class="uprompt-dropzone__title">Bilder hochladen</div>
          <div class="uprompt-dropzone__sub">Klicken oder Dateien hierher ziehen</div>
          <div class="uprompt-dropzone__hint">JPG · PNG · TIFF · WEBP</div>
        </div>
        <div v-if="uploadPromptSource === 'phone'" class="uprompt-phone">
          <div class="uprompt-phone-qr">
            <img v-if="phoneQrDataUrl" :src="phoneQrDataUrl" alt="QR Code für Handy-Upload" />
            <div v-else class="uprompt-phone-qrph">{{ phoneQrBusy ? 'QR wird geladen…' : 'Kein QR verfügbar' }}</div>
          </div>
          <div class="uprompt-phone-copy">
            <div class="uprompt-phone-head">
              <div>
                <div class="uprompt-phone-title">Mit dem Handy hochladen</div>
                <div class="uprompt-phone-sub">QR scannen und Bilder direkt in diese Kategorie und Produkt-Auswahl laden.</div>
                <div v-if="phoneUploadDetectedCount > 0" class="uprompt-phone-ok">
                  {{ phoneUploadDetectedCount }} Bild(er) wurden erkannt.
                </div>
              </div>
            </div>
            <div class="uprompt-phone-actions">
              <button class="btn" type="button" @click="copyPhoneUploadUrl">
                <Copy :size="16" />
                Link kopieren
              </button>
            </div>
          </div>
        </div>
        <div class="uprompt-actions">
          <button class="btn" type="button" @click="closeUploadPrompt">Abbrechen</button>
        </div>
      </div>
    </div>
  </Teleport>

  <LeaveEditorConfirmModal
    :open="!!leaveConfirmTarget"
    @close="leaveConfirmTarget = null"
    @confirm="confirmLeaveEditor"
  />

  <div class="entry-upload-hidden" aria-hidden="true">
    <UploadBox
      ref="entryUploadRef"
      :product-id="selectedEntryProductId || 0"
      :category-id="selectedEntryCategoryId || 0"
      @uploaded="onUploadBuffered"
    />
  </div>

  <PostUploadOverlay
    v-if="showPostUploadOverlay"
    :files="pendingUploadFiles"
    :initial-category-id="selectedEntryCategoryId ?? undefined"
    :initial-product-id="effectiveSelectedEntryProductId ?? undefined"
    :current-editor-type="resolvedEditorType"
    @apply="onPostUploadApply"
    @cancel="onPostUploadCancel"
  />
</template>

<script setup lang="ts">
import {computed, nextTick, onBeforeUnmount, ref, watch, watchEffect} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import type { EditorCategoryTreeItem, EditorCategoryTreeResponse } from '../../services/editorCategoryApi'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../services/editorCatalogApi'
import { fetchAdobeStockPreviewFile } from '../../services/stockImportService'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { buildPrintboxEditorUrl, resolvePrintboxFamilyId } from '../../utils/printboxCategory'
import { buildMobileUploadUrl, MOBILE_UPLOAD_QR_OPTIONS } from '../../utils/mobileUploadLink'
import UploadBox from '../editor/utils/upload-box.vue'
import PostUploadOverlay from '../editor/PostUploadOverlay.vue'
import LeaveEditorConfirmModal from '../editor/LeaveEditorConfirmModal.vue'
import { useProjectStore } from '../../store/useProjectStore'
import { useTransientUploadStore } from '../../store/useTransientUploadStore'
import { useEntryUploadHandoffStore } from '../../store/useEntryUploadHandoffStore'
import { createEditorPreview, createThumb, readImageDimensions } from '../../utils/imageOps'
import { buildMaxCropForRecord } from '../../utils/cropPreset'
import { emitUploadEvent } from '../../utils/uploadBus'
import { uploadImageRecordsBatch } from '../../services/uploadService'
import { fetchProjectUploads, invalidateProjectUploadsCache, mapRemoteUploadToRecordMeta } from '../../services/projectUploadBridge'
import { subscribeToUploadEvents } from '../../services/uploadEvents'
import { syncUploadMetaBatch } from '../../services/uploadMetaApi'
import type { ImageRecord } from '../../utils/imageDb'
import QRCode from 'qrcode'
import { Monitor, Smartphone, Copy, Upload, Image as ImageIcon, Sparkles, ArrowRight } from 'lucide-vue-next'
import { offersDesigner, resolveEditorConfig } from '../../utils/editorConfigResolver'

type CategoryVm = {
  id: string
  _id: number
  title: string
  image: string
  productCount?: number | null
  iri: string
  editorType?: 'single' | 'multi' | null
  editorConfig?: any
  isPrintbox?: boolean
}

type ProductVm = {
  id: string
  _id: number
  title: string
  shortDescription?: string
  image: string
}

const props = defineProps<{
  parentCategoryId?: string | number | null
  categoryId?: number | null
  productId?: number | null
  autoNavigateToEditor?: boolean
}>()

const emit = defineEmits<{
  (e: 'category:selected', payload: { categoryId: number; title: string }): void
  (e: 'product:selected', payload: { productId: number; title: string }): void
}>()

const route = useRoute()
const router = useRouter()
const catalogBootstrap = ref<EditorCatalogBootstrapResponse | null>(null)
const project = useProjectStore()
const transientUploads = useTransientUploadStore()
const entryUploadHandoff = useEntryUploadHandoffStore()
const categoryTree = ref<EditorCategoryTreeResponse | null>(null)
const entryUploadRef = ref<{ openPicker: () => void; handleFiles: (files: FileList | File[]) => void } | null>(null)
const showUploadPrompt = ref(false)
const showPostUploadOverlay = ref(false)
const selectedEntryProductId = ref<number | null>(null)
const selectedEntryProductTitle = ref<string | null>(null)
const pendingUploadFiles = ref<Array<{
  id: string
  file: File | null
  fileName: string
  previewUrl: string
  width?: number
  height?: number
  assetId?: string
  stockAssetId?: string
  remoteUpload?: boolean
  remoteItem?: any
}>>([])
const leaveConfirmTarget = ref<string | null>(null)
const stockImportBusy = ref(false)
const handledStockImportKeys = new Set<string>()
const activeOpenDrawerKey = ref<string | null>(null)
const uploadPromptSource = ref<'desktop' | 'phone'>('desktop')
const phoneUploadUrl = ref('')
const phoneQrDataUrl = ref('')
const phoneQrBusy = ref(false)
const phoneUploadDetectedCount = ref(0)
const isDragging = ref(false)
const phoneRemoteWatchBusy = ref(false)
const phoneRemoteLastSyncAt = ref<string>('')
const phoneRemoteSeenIds = new Set<string>()
const phoneRemoteHandledIds = new Set<string>()
const phoneRemoteWatchCategoryId = ref<number | null>(null)
let phoneRemotePollTimer: ReturnType<typeof setInterval> | null = null
let stopPhoneMercure: null | (() => void) = null
const entryLogoUrl = computed(() => {
  try {
    return String(getWidgetConfig().logoUrl || '').trim()
  } catch {
    return ''
  }
})
const overviewLinkHref = computed(() => {
  try {
    return String(getWidgetConfig().overviewUrl || '/').trim() || '/'
  } catch {
    return '/'
  }
})

type Step = 'parents' | 'children' | 'products'
const step = ref<Step>('parents')

/* ---------- helpers ---------- */
function absApiUrl(src?: string | null) {
  const placeholder = 'https://via.placeholder.com/640x360?text=Bild'
  if (!src) return placeholder
  return src.startsWith('http') ? src : `https://www.pixelfoto-express.de${src}`
}

function qp(key: string): string | null {
  const v = route.query[key]
  return v == null ? null : String(Array.isArray(v) ? v[0] : v)
}
function qn(key: string): number | null {
  const v = qp(key)
  if (v == null) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

const isStockImportContext = computed<boolean>(() => {
  const assetId = qp('assetId')
  const stockAssetId = qp('stockAssetId')
  const stockContinue = qp('stockContinue')
  return !!(assetId || stockAssetId || stockContinue)
})

async function setQuery(patch: Record<string, any>) {
  const q: any = { ...route.query }
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === '') delete q[k]
    else q[k] = v
  }
  await router.replace({ query: q })
}

async function clearQuery(keys: string[]) {
  const q: any = { ...route.query }
  keys.forEach((k) => delete q[k])
  await router.replace({ query: q })
}

function mapCategoryVm(c: any): CategoryVm {
  return {
    id: c.id,
    _id: Number(String(c._id ?? String(c.id).split('/').pop())),
    title: c.title,
    image: absApiUrl(c.thumbnailWebp || c.thumbnail),
    productCount: c.productCount ?? null,
    iri: String(c.id),
    editorType: c.editorType ?? null,
  }
}

async function loadCategoryTree() {
  const payload = await fetchEditorCatalogBootstrap({
    editorBaseUrl: getWidgetConfig().editorBaseUrl,
  })
  catalogBootstrap.value = payload
  categoryTree.value = payload.tree
}

/* ---------- resolve from props/query ---------- */
const resolvedParentId = computed(() => {
  const raw = qp('parent') ?? qp('id') ?? props.parentCategoryId
  return raw != null ? String(raw) : null
})
const resolvedParentIri = computed(() => (resolvedParentId.value ? `/api/categories/${resolvedParentId.value}` : null))

const resolvedChildId = computed<number | null>(() => {
  const raw = qn('category') ?? qn('categoryId') ?? props.categoryId
  return raw != null ? Number(raw) : null
})
const resolvedChildIri = computed<string | null>(() => {
  if (resolvedChildId.value) return `/api/categories/${resolvedChildId.value}`
  return null
})

const resolvedProductId = computed<number | null>(() => {
  const raw = qn('product') ?? qn('productId') ?? props.productId
  return raw != null ? Number(raw) : null
})
/* ---------- local selection ---------- */
const parentCat = ref<CategoryVm | null>(null)
const childCat = ref<CategoryVm | null>(null)
const headline = computed(() => {
  if (step.value === 'products') return childCat.value?.title || parentCat.value?.title || 'Produkte'
  if (step.value === 'children') return parentCat.value?.title || 'Unterkategorien'
  return 'Produkteditor'
})
const effectiveEditorCategoryId = computed<number | null>(() => {
  const localCategoryId = Number(selectedEntryCategoryId.value || 0)
  if (localCategoryId > 0) return localCategoryId
  const queryCategoryId = Number(resolvedChildId.value || resolvedParentId.value || 0)
  return queryCategoryId > 0 ? queryCategoryId : null
})

const canOpenMultiEditor = computed(() => true)
const breadcrumbItems = computed(() => {
  const items: Array<{ label: string; action?: () => void }> = []
  items.push({ label: 'Übersicht', action: step.value === 'parents' ? undefined : goToParents })
  if (step.value === 'parents') return items
  if (parentCat.value) {
    items.push({ label: parentCat.value.title, action: step.value === 'products' ? goToChildren : undefined })
  }
  if (step.value === 'products' && childCat.value && childCat.value._id !== parentCat.value?._id) {
    items.push({ label: childCat.value.title })
  }
  return items
})

async function openMultiEditor() {
  if (!canOpenMultiEditor.value) return
  const effectiveProductId = Number(effectiveSelectedEntryProductId.value || 0) || null
  const effectiveCategoryId = Number(effectiveEditorCategoryId.value || 0) || null
  const preferredProjectCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()
  await router.push({
    path: '/multi',
    query: {
      ...route.query,
      editorType: 'multi',
      ...(preferredProjectCode ? { projectCode: preferredProjectCode } : {}),
      ...(effectiveCategoryId ? {
        category: String(effectiveCategoryId),
        categoryId: String(effectiveCategoryId),
      } : {}),
      ...(effectiveProductId ? {
        product: String(effectiveProductId),
        productId: String(effectiveProductId),
      } : {}),
    },
  })
}

function visibleChildrenFor(parentIdRaw: number | string | null | undefined) {
  const parentId = Number(parentIdRaw || 0)
  if (!(parentId > 0)) return []
  const nodes = Array.isArray(categoryTree.value?.items) ? categoryTree.value.items : []
  return nodes.filter((c: any) => Number(c?.parentId ?? 0) === parentId && c?.showInEditor !== false)
}

/* =====================================================================================
 *  ✅ INITIAL LOAD BY ID (WICHTIG)
 *  Wenn categoryId oder parentId aus Config/URL kommt: Category nachladen!
 * ===================================================================================== */

watch([categoryTree, resolvedChildId, resolvedParentId], async ([tree, childIdRaw, parentIdRaw]) => {
  const items = Array.isArray((tree as any)?.items) ? (tree as any).items : []
  const childId = Number(childIdRaw || 0)
  const parentId = Number(parentIdRaw || 0)

  if (childId > 0) {
    const c: any = items.find((item: any) => Number(item?._id) === childId)
    if (!c) return
    const visibleChildren = visibleChildrenFor(c._id)
    if (visibleChildren.length > 0) {
      parentCat.value = mapCategoryVm(c)
      childCat.value = null
      step.value = 'children'
      return
    }
    childCat.value = mapCategoryVm(c)
    step.value = 'products'
    if (c.parent) {
      parentCat.value = mapCategoryVm(c.parent)
    } else {
      parentCat.value = mapCategoryVm(c)
    }
    return
  }

  if (parentId > 0) {
    const c: any = items.find((item: any) => Number(item?._id) === parentId)
    if (!c) return
    parentCat.value = mapCategoryVm(c)
    step.value = 'children'
  }
}, { immediate: true })

const productByIdData = computed(() => {
  const id = Number(resolvedProductId.value || 0)
  if (!(id > 0) || resolvedChildIri.value) return null
  return catalogBootstrap.value?.products?.[String(id)] ?? null
})

const effectiveSelectedEntryProductId = computed<number | null>(() => {
  const localId = Number(selectedEntryProductId.value || 0)
  if (localId > 0) return localId
  const resolvedId = Number(resolvedProductId.value || 0)
  return resolvedId > 0 ? resolvedId : null
})

const effectiveSelectedEntryProductTitle = computed<string | null>(() => {
  const localTitle = String(selectedEntryProductTitle.value || '').trim()
  if (localTitle) return localTitle
  const fromBootstrap = String((productByIdData.value as any)?.title || '').trim()
  return fromBootstrap || null
})

watch(
  () => productByIdData.value,
  async (p: any) => {
    if (!p || resolvedChildIri.value) return

    const cats = Array.isArray(p?.categories) ? p.categories : []
    const chosenCat = cats[0] ?? null
    if (!chosenCat?._id || !chosenCat?.id) return

    childCat.value = {
      id: String(chosenCat.id),
      _id: Number(chosenCat._id),
      title: String(chosenCat.title || ''),
      image: absApiUrl((chosenCat as any).thumbnailWebp || (chosenCat as any).thumbnail),
      productCount: null,
      iri: String(chosenCat.id),
      editorType: chosenCat.editorType ?? null,
    }
    parentCat.value = chosenCat.parent ? {
      id: String(chosenCat.parent.id),
      _id: Number(chosenCat.parent._id),
      title: String(chosenCat.parent.title || ''),
      image: absApiUrl(chosenCat.parent.thumbnailWebp || chosenCat.parent.thumbnail),
      productCount: null,
      iri: String(chosenCat.parent.id),
      editorType: chosenCat.parent.editorType ?? null,
    } : childCat.value
    step.value = 'products'

    await setQuery({
      category: String(childCat.value._id),
      categoryId: String(childCat.value._id),
      editorType: childCat.value.editorType ?? null,
      product: String(p._id),
    })
  },
  { immediate: true },
)

/* ---------- Parents categories ---------- */
watchEffect(() => {
  if (!categoryTree.value) loadCategoryTree().catch(() => {})
})
const parentsLoading = computed(() => !categoryTree.value)
const parentsError = ref<any>(null)
async function refreshParents() {
  try {
    await loadCategoryTree()
  } catch (err) {
    parentsError.value = err
  }
}

const parentCategories = computed<CategoryVm[]>(() => {
  const nodes = Array.isArray(categoryTree.value?.roots) ? categoryTree.value!.roots : []
  return nodes
    .filter((c: any) => c?.showInEditor !== false || resolvePrintboxFamilyId(c) !== null)
    .sort((a: any, b: any) => {
      const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
      if (pos !== 0) return pos
      return String(a?.title || '').localeCompare(String(b?.title || ''), 'de')
    })
    .map((c: any) => ({
    id: c.id,
    _id: c._id,
    title: c.title,
    image: absApiUrl(c.thumbnailWebp || c.thumbnail),
    productCount: c.productCount ?? null,
    iri: String(c.id),
    editorType: c.editorType ?? null,
    editorConfig: c.editorConfig ?? null,
    isPrintbox: resolvePrintboxFamilyId(c) !== null,
  }))
})

const standardParentCategories = computed<CategoryVm[]>(() =>
  parentCategories.value.filter((c) => !c.isPrintbox),
)

const designerCategories = computed<CategoryVm[]>(() =>
  isStockImportContext.value ? [] : parentCategories.value.filter((c) => c.isPrintbox),
)

/* ---------- Children categories ---------- */
const activeParentIri = computed(() => parentCat.value?.iri ?? resolvedParentIri.value ?? null)
const childrenLoading = computed(() => !!activeParentIri.value && !categoryTree.value)
const childrenError = ref<any>(null)
async function refreshChildren() {
  try {
    await loadCategoryTree()
  } catch (err) {
    childrenError.value = err
  }
}

const childCategories = computed<CategoryVm[]>(() => {
  const activeParentId = Number(parentCat.value?._id ?? resolvedParentId.value ?? 0)
  return visibleChildrenFor(activeParentId)
    .sort((a: any, b: any) => {
      const pos = Number(a?.position ?? 0) - Number(b?.position ?? 0)
      if (pos !== 0) return pos
      return String(a?.title || '').localeCompare(String(b?.title || ''), 'de')
    })
    .map((c: any) => ({
    id: c.id,
    _id: c._id,
    title: c.title,
    image: absApiUrl(c.thumbnailWebp || c.thumbnail),
    productCount: c.productCount ?? null,
    iri: String(c.id),
    editorType: c.editorType ?? null,
  }))
})

/* ---------- Products (ONLY if child iri exists) ---------- */
const activeCategoryIriForProducts = computed(() => childCat.value?.iri ?? resolvedChildIri.value ?? null)
const productsEnabled = computed(() => !!activeCategoryIriForProducts.value)
const prodLoading = computed(() => false)
const prodError = ref<any>(null)
async function refreshProducts() {}

const products = computed<ProductVm[]>(() => {
  const categoryId = Number(childCat.value?._id ?? resolvedChildId.value ?? 0)
  if (!(categoryId > 0)) return []
  const ids = catalogBootstrap.value?.productsByCategory?.[String(categoryId)] ?? []
  const nodes = ids
    .map((id: any) => catalogBootstrap.value?.products?.[String(Number(id || 0))])
    .filter(Boolean)
  return nodes
    .filter((p: any) => {
      const cats = Array.isArray(p?.categories) ? p.categories : []
      if (!cats.length) return false
      return cats.some((c: any) => Number(c?._id) === categoryId && c?.showInEditor === true)
    })
    .map((p: any) => ({
    id: p.id,
    _id: p._id,
    title: p.title,
    shortDescription: p.shortDescription,
    image: absApiUrl(p.thumbnailWebp || p.thumbnail),
  }))
})

const isBusy = computed(() => parentsLoading.value || childrenLoading.value || prodLoading.value)

/* ---------- editorType sauber bestimmen ---------- */
const resolvedEditorType = computed<'single' | 'multi'>(() => {
  const fromCat = childCat.value?.editorType
  if (fromCat === 'single' || fromCat === 'multi') return fromCat
  const q = qp('editorType')
  if (q === 'single' || q === 'multi') return q
  return 'single'
})

/* ---------- restore step from URL ---------- */
watchEffect(() => {
  if (resolvedChildId.value) {
    step.value = 'products'
    return
  }
  if (resolvedParentId.value) {
    step.value = 'children'
    return
  }
  step.value = 'parents'
})

watch(activeParentIri, async (iri) => {
  if (!iri) return
  await refreshChildren()
})

// Auto-Skip: Wenn Eltern-Kategorie keine Unterkategorien hat → direkt zu Produkten
watch(
  [childCategories, childrenLoading],
  ([cats, loading]) => {
    if (loading) return
    if (cats.length === 0 && step.value === 'children' && parentCat.value && !resolvedChildId.value) {
      childCat.value = parentCat.value
      step.value = 'products'
    }
  },
)

watch(activeCategoryIriForProducts, async (iri) => {
  if (!iri) return
  step.value = 'products'
  await refreshProducts()
})

const selectedEntryCategoryId = computed<number | null>(() => childCat.value?._id ?? resolvedChildId.value ?? parentCat.value?._id ?? null)
const selectedEntryCategoryTitle = computed<string | null>(() => childCat.value?.title ?? parentCat.value?.title ?? null)
const selectedEntryCategoryThumbnail = computed<string | null>(() => childCat.value?.image ?? parentCat.value?.image ?? null)

function persistedSizeValue(input: any) {
  if (!input || typeof input !== 'object') return null
  const id = String(input?.id || input?.sizeId || '').trim() || undefined
  const variantId = String(input?.variantId || '').trim() || undefined
  const title = String(input?.title || '').trim() || undefined
  const widthMm = Number(input?.widthMm || 0) || undefined
  const heightMm = Number(input?.heightMm || 0) || undefined
  if (!id && !variantId && !title && !widthMm && !heightMm) return null
  return {
    ...(id ? { id } : {}),
    ...(variantId ? { variantId } : {}),
    ...(title ? { title } : {}),
    ...(widthMm ? { widthMm } : {}),
    ...(heightMm ? { heightMm } : {}),
  }
}

async function buildPlaceholderRecordForEntryUpload(
  fileLike: { id: string; file: File; fileName: string; previewUrl: string; width?: number; height?: number },
  payload: {
    productId: number
    productTitle?: string
    categoryId: number
    categoryTitle?: string
    categoryThumbnail?: string
    sizeVariantId?: string
    sizeTitle?: string
    sizeWidthMm?: number
    sizeHeightMm?: number
    options: Array<{ groupId: string; variantId: string; value: string }>
  },
  projectCode: string | null,
): Promise<ImageRecord> {
  const sourceBlob = fileLike.file
  const createdAt = Date.now()
  const knownDims = (
    Number((fileLike as any)?.width || 0) > 0 && Number((fileLike as any)?.height || 0) > 0
      ? { width: Number((fileLike as any).width), height: Number((fileLike as any).height) }
      : await readImageDimensions(sourceBlob).catch(() => ({ width: 0, height: 0 }))
  )
  const size = (payload.sizeVariantId || payload.sizeTitle)
    ? persistedSizeValue({
        variantId: payload.sizeVariantId,
        title: payload.sizeTitle,
        widthMm: payload.sizeWidthMm,
        heightMm: payload.sizeHeightMm,
      })
    : null

  const initialCrop = (size && Number(knownDims.width || 0) > 0 && Number(knownDims.height || 0) > 0)
    ? buildMaxCropForRecord({
        width: Math.max(0, Number(knownDims.width || 0)),
        height: Math.max(0, Number(knownDims.height || 0)),
        editorWidth: Math.max(0, Number(knownDims.width || 0)),
        editorHeight: Math.max(0, Number(knownDims.height || 0)),
      } as any, size)
    : null
  const initialStoredCrop = initialCrop
    ? {
        x: initialCrop.x,
        y: initialCrop.y,
        w: initialCrop.w,
        h: initialCrop.h,
        sourceWidth: Math.max(1, Number(initialCrop.sourceWidth || knownDims.width || 1)),
        sourceHeight: Math.max(1, Number(initialCrop.sourceHeight || knownDims.height || 1)),
        nx: Number(initialCrop.nx || 0),
        ny: Number(initialCrop.ny || 0),
        nw: Number(initialCrop.nw || 0),
        nh: Number(initialCrop.nh || 0),
      }
    : undefined

  const jobs = await Promise.allSettled([
    createThumb(sourceBlob, 280, 'image/jpeg', 0.86),
    createEditorPreview(sourceBlob, 1600, 'image/webp', 0.82),
  ])
  const thumb = jobs[0]?.status === 'fulfilled' ? jobs[0].value : null
  const editorPreview = jobs[1]?.status === 'fulfilled' ? jobs[1].value : null

  return {
    id: fileLike.id,
    name: fileLike.fileName,
    createdAt,
    updatedAt: createdAt,
    mime: String(sourceBlob.type || 'application/octet-stream'),
    bytes: Number(sourceBlob.size || 0),
    width: Math.max(0, Number(knownDims.width || 0)),
    height: Math.max(0, Number(knownDims.height || 0)),
    originalBlob: sourceBlob,
    workingBlob: sourceBlob,
    ...(editorPreview ? {
      editorBlob: editorPreview.blob,
      editorWidth: editorPreview.width,
      editorHeight: editorPreview.height,
    } : {
      editorWidth: Math.max(0, Number(knownDims.width || 0)),
      editorHeight: Math.max(0, Number(knownDims.height || 0)),
    }),
    ...(thumb ? {
      thumbBlob: thumb.blob,
      thumbWidth: thumb.width,
      thumbHeight: thumb.height,
    } : {}),
    productId: payload.productId,
    ...(payload.productTitle ? ({ productTitle: String(payload.productTitle) } as any) : {}),
    categoryId: payload.categoryId,
    ...(payload.categoryTitle ? ({ categoryTitle: String(payload.categoryTitle) } as any) : {}),
    ...(payload.categoryThumbnail ? ({ categoryThumbnail: String(payload.categoryThumbnail) } as any) : {}),
    ...(projectCode ? { projectCode } : {}),
    edits: {
      ...(initialStoredCrop ? { crop: initialStoredCrop } : {}),
      rotate: 0,
      lockAR: true,
      swapAR: false,
      orientMode: 'auto',
      preset: {
        productId: payload.productId,
        ...(payload.productTitle ? ({ productTitle: String(payload.productTitle) } as any) : {}),
        ...(size ? { sizeId: String(size.id || '').trim() || undefined, size } : {}),
        options: Array.isArray(payload.options) ? payload.options : [],
        quantity: 1,
      },
    } as any,
    upload: {
      state: 'queued',
      phase: 'queued',
      progressPct: 0,
      uploadedBytes: 0,
      totalBytes: Number(sourceBlob.size || 0) || 0,
      tries: 0,
      updatedAt: createdAt,
    },
  }
}

function buildFastPlaceholderRecordForEntryUpload(
  fileLike: { id: string; file: File; fileName: string; previewUrl: string; width?: number; height?: number },
  payload: {
    productId: number
    productTitle?: string
    categoryId: number
    categoryTitle?: string
    categoryThumbnail?: string
    sizeVariantId?: string
    sizeTitle?: string
    sizeWidthMm?: number
    sizeHeightMm?: number
    options: Array<{ groupId: string; variantId: string; value: string }>
  },
  projectCode: string | null,
): ImageRecord {
  const sourceBlob = fileLike.file
  const createdAt = Date.now()
  const fastWidth = Math.max(0, Number((fileLike as any)?.width || 0))
  const fastHeight = Math.max(0, Number((fileLike as any)?.height || 0))
  const size = (payload.sizeVariantId || payload.sizeTitle)
    ? persistedSizeValue({
        variantId: payload.sizeVariantId,
        title: payload.sizeTitle,
        widthMm: payload.sizeWidthMm,
        heightMm: payload.sizeHeightMm,
      })
    : null

  return {
    id: fileLike.id,
    name: fileLike.fileName,
    createdAt,
    updatedAt: createdAt,
    mime: String(sourceBlob.type || 'application/octet-stream'),
    bytes: Number(sourceBlob.size || 0),
    width: fastWidth || undefined,
    height: fastHeight || undefined,
    originalBlob: sourceBlob,
    workingBlob: sourceBlob,
    editorWidth: fastWidth || undefined,
    editorHeight: fastHeight || undefined,
    productId: payload.productId,
    ...(payload.productTitle ? ({ productTitle: String(payload.productTitle) } as any) : {}),
    categoryId: payload.categoryId,
    ...(payload.categoryTitle ? ({ categoryTitle: String(payload.categoryTitle) } as any) : {}),
    ...(payload.categoryThumbnail ? ({ categoryThumbnail: String(payload.categoryThumbnail) } as any) : {}),
    ...(projectCode ? { projectCode } : {}),
    edits: {
      rotate: 0,
      preset: {
        productId: payload.productId,
        ...(payload.productTitle ? ({ productTitle: String(payload.productTitle) } as any) : {}),
        ...(size ? { sizeId: String(size.id || '').trim() || undefined, size } : {}),
        options: payload.options || [],
        quantity: 1,
      },
      lockAR: true,
      swapAR: false,
      orientMode: 'auto',
    } as any,
    upload: {
      state: 'queued',
      phase: 'queued',
      progressPct: 0,
      uploadedBytes: 0,
      totalBytes: Number(sourceBlob.size || 0) || 0,
      previewUrl: String(fileLike.previewUrl || ''),
      thumbUrl: String(fileLike.previewUrl || ''),
      fullscreenPreviewUrl: String(fileLike.previewUrl || ''),
      tries: 0,
      updatedAt: createdAt,
    },
  }
}

/* ---------- Designer als Alternative (editorConfig.designAvailable) ---------- */
const designerOpening = ref(false)
const designerOffered = computed(() => {
  const pid = Number(effectiveSelectedEntryProductId.value || 0)
  if (!(pid > 0)) return false
  const product = catalogBootstrap.value?.products?.[String(pid)] ?? null
  if (!product) return false
  return offersDesigner(resolveEditorConfig({ product: product as any, categoryId: effectiveEditorCategoryId.value }))
})

watch(showUploadPrompt, (open) => {
  // Katalog wird für die Designer-Prüfung gebraucht; beim Direkteinstieg (openDrawer) ggf. nachladen.
  if (open && !catalogBootstrap.value) loadCategoryTree().catch(() => {})
})

async function openDesignerFromPrompt() {
  const productId = Number(effectiveSelectedEntryProductId.value || 0)
  const categoryId = Number(effectiveEditorCategoryId.value || 0)
  if (!(productId > 0) || designerOpening.value) return
  designerOpening.value = true
  try {
    const projectCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()
    closeUploadPrompt()
    await router.push({
      path: '/design',
      query: {
        productId: String(productId),
        ...(categoryId > 0 ? { categoryId: String(categoryId) } : {}),
        ...(projectCode ? { projectCode } : {}),
      },
    })
  } finally {
    designerOpening.value = false
  }
}

function closeUploadPrompt() {
  showUploadPrompt.value = false
  isDragging.value = false
  phoneUploadDetectedCount.value = 0
}

function confirmEmptyCategoryUploadPrompt() {
  entryUploadRef.value?.openPicker()
  showUploadPrompt.value = false
}

async function buildPhoneBridge() {
  const code = await project.ensureProject().catch(() => '')
  const categoryId = Number(selectedEntryCategoryId.value || 0)
  const productId = Number(selectedEntryProductId.value || 0)
  if (!code) {
    phoneUploadUrl.value = ''
    phoneQrDataUrl.value = ''
    return
  }

  phoneQrBusy.value = true
  try {
    phoneUploadUrl.value = buildMobileUploadUrl({ projectCode: code, categoryId, productId })
    phoneQrDataUrl.value = await QRCode.toDataURL(phoneUploadUrl.value, MOBILE_UPLOAD_QR_OPTIONS)
  } finally {
    phoneQrBusy.value = false
  }
}

async function copyPhoneUploadUrl() {
  if (!phoneUploadUrl.value) await buildPhoneBridge()
  if (!phoneUploadUrl.value || typeof navigator === 'undefined' || !navigator.clipboard) return
  await navigator.clipboard.writeText(phoneUploadUrl.value)
}

function stopPhoneUploadWatch() {
  if (stopPhoneMercure) stopPhoneMercure()
  stopPhoneMercure = null
  if (phoneRemotePollTimer) clearInterval(phoneRemotePollTimer)
  phoneRemotePollTimer = null
  phoneRemoteWatchCategoryId.value = null
}

function startPhoneUploadPolling() {
  if (phoneRemotePollTimer) clearInterval(phoneRemotePollTimer)
  phoneRemotePollTimer = setInterval(() => {
    void syncPhoneRemoteUploads()
  }, 3500)
}

async function consumePhoneRemoteUploads(items: any[]) {
  const freshItems = (Array.isArray(items) ? items : []).filter((item: any) => {
    const id = String(item?.id || '').trim()
    if (!id || phoneRemoteSeenIds.has(id) || phoneRemoteHandledIds.has(id)) return false
    phoneRemoteSeenIds.add(id)
    return true
  })

  if (!freshItems.length) return

  const nextFiles = freshItems
    .map((item: any) => {
      const id = String(item?.id || '').trim()
      if (!id) return null
      return {
        id,
        file: null,
        fileName: String(item?.name || id),
        previewUrl: String(item?.thumbUrl || item?.previewUrl || item?.fullscreenPreviewUrl || '').trim(),
        width: Number(item?.width || 0) || undefined,
        height: Number(item?.height || 0) || undefined,
        remoteUpload: true,
        remoteItem: item,
      }
    })
    .filter(Boolean) as any[]

  const byId = new Map<string, any>()
  for (const file of pendingUploadFiles.value as any[]) {
    const id = String(file?.id || '').trim()
    if (!id) continue
    byId.set(id, file)
  }
  for (const file of nextFiles) {
    const id = String(file?.id || '').trim()
    if (!id) continue
    byId.set(id, {
      ...(byId.get(id) || {}),
      ...file,
    })
  }

  phoneUploadDetectedCount.value += freshItems.length
  pendingUploadFiles.value = [...byId.values()] as any
  showUploadPrompt.value = false
  showPostUploadOverlay.value = true
}

async function syncPhoneRemoteUploads() {
  const phonePromptOpen = showUploadPrompt.value && uploadPromptSource.value === 'phone'
  const remoteOverlayOpen = showPostUploadOverlay.value && pendingUploadFiles.value.some((file: any) => file?.remoteUpload === true)
  if ((!phonePromptOpen && !remoteOverlayOpen) || phoneRemoteWatchBusy.value) return
  const categoryId = Number(selectedEntryCategoryId.value || 0)
  if (!(categoryId > 0)) return
  const code = await project.ensureProject().catch(() => '')
  if (!code) return

  phoneRemoteWatchBusy.value = true
  try {
    const cfg = getWidgetConfig() as any
    invalidateProjectUploadsCache({ editorBaseUrl: cfg.editorBaseUrl, projectCode: code })
    const items = await fetchProjectUploads({
      editorBaseUrl: cfg.editorBaseUrl,
      projectCode: code,
      categoryId,
      source: 'phone',
      statuses: ['done'],
      since: phoneRemoteLastSyncAt.value || null,
      limit: 50,
    })
    if (items.length) {
      phoneRemoteLastSyncAt.value = String(items[0]?.createdAt || phoneRemoteLastSyncAt.value || '')
      await consumePhoneRemoteUploads(items)
    }
  } catch {
    // polling stays silent
  } finally {
    phoneRemoteWatchBusy.value = false
  }
}

async function startPhoneUploadWatch() {
  const categoryId = Number(selectedEntryCategoryId.value || 0)
  if (!(categoryId > 0)) return
  if (phoneRemoteWatchCategoryId.value === categoryId && (stopPhoneMercure || phoneRemotePollTimer)) {
    await syncPhoneRemoteUploads()
    return
  }

  stopPhoneUploadWatch()
  phoneRemoteSeenIds.clear()
  phoneRemoteLastSyncAt.value = ''
  phoneUploadDetectedCount.value = 0
  const code = await project.ensureProject().catch(() => '')
  if (!code) return

  const cfg = getWidgetConfig() as any
  try {
    invalidateProjectUploadsCache({ editorBaseUrl: cfg.editorBaseUrl, projectCode: code })
    const existingItems = await fetchProjectUploads({
      editorBaseUrl: cfg.editorBaseUrl,
      projectCode: code,
      categoryId,
      source: 'phone',
      statuses: ['done'],
      limit: 50,
    })
    for (const item of existingItems) {
      const id = String(item?.id || '').trim()
      if (!id) continue
      phoneRemoteSeenIds.add(id)
    }
    if (existingItems.length) {
      phoneRemoteLastSyncAt.value = String(existingItems[0]?.createdAt || '')
    }
  } catch {
    phoneRemoteLastSyncAt.value = ''
  }
  phoneRemoteWatchCategoryId.value = categoryId
  stopPhoneMercure = subscribeToUploadEvents({
    editorBaseUrl: cfg.editorBaseUrl,
    mercureHubUrl: cfg.mercureHubUrl,
    projectCode: code,
    categoryId,
    onDeleted: async () => {
      await syncPhoneRemoteUploads()
    },
    onUpload: async (item) => {
      if (String(item?.source || '').trim() !== 'phone') return
      if (String(item?.status || '').trim() !== 'done' && item?.processing !== false) return
      await syncPhoneRemoteUploads()
    },
    onError: () => {
      startPhoneUploadPolling()
    },
  })

  if (!stopPhoneMercure) startPhoneUploadPolling()
  await syncPhoneRemoteUploads()
}

function revokePendingUploadFiles() {
  for (const file of pendingUploadFiles.value) {
    try {
      if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl)
    } catch {}
  }
}

async function buildOverlayPreviewFileState(input: any) {
  const file = (input?.blob || input?.file) as File | undefined
  if (!file) return null

  let previewUrl = String(input?.previewUrl || '').trim()
  const width = Number(input?.width || 0) || undefined
  const height = Number(input?.height || 0) || undefined

  if (!previewUrl) {
    try {
      previewUrl = URL.createObjectURL(file)
    } catch {
      previewUrl = ''
    }
  }

  return {
    id: String(input?.id || crypto.randomUUID()),
    file,
    fileName: String(input?.fileName || input?.blob?.name || input?.file?.name || 'Foto'),
    previewUrl,
    width,
    height,
  }
}

const pendingUploadPreviewUpgradeQueue: Array<() => Promise<void>> = []
let pendingUploadPreviewUpgradeActive = 0
const PENDING_UPLOAD_PREVIEW_UPGRADE_CONCURRENCY = 3

function pumpPendingUploadPreviewUpgradeQueue() {
  while (
    pendingUploadPreviewUpgradeActive < PENDING_UPLOAD_PREVIEW_UPGRADE_CONCURRENCY &&
    pendingUploadPreviewUpgradeQueue.length > 0
  ) {
    const task = pendingUploadPreviewUpgradeQueue.shift()
    if (!task) return
    pendingUploadPreviewUpgradeActive += 1
    void task().finally(() => {
      pendingUploadPreviewUpgradeActive = Math.max(0, pendingUploadPreviewUpgradeActive - 1)
      pumpPendingUploadPreviewUpgradeQueue()
    })
  }
}

function enqueuePendingUploadPreviewUpgrade(task: () => Promise<void>) {
  pendingUploadPreviewUpgradeQueue.push(task)
  pumpPendingUploadPreviewUpgradeQueue()
}

async function upgradePendingUploadPreviewFileState(item: any) {
  const file = item?.file as File | undefined
  if (!file) return

  let previewUrl = String(item?.previewUrl || '').trim()
  let width = Number(item?.width || 0) || undefined
  let height = Number(item?.height || 0) || undefined

  const preview = await createEditorPreview(file, 1600, 'image/webp', 0.82).catch(() => null)
  if (preview?.blob) {
    previewUrl = URL.createObjectURL(preview.blob)
    width = preview.width || width
    height = preview.height || height
  } else {
    const thumb = await createThumb(file, 560, 'image/jpeg', 0.86).catch(() => null)
    if (thumb?.blob) {
      previewUrl = URL.createObjectURL(thumb.blob)
      width = thumb.width || width
      height = thumb.height || height
    }
  }

  if (!(Number(width || 0) > 0) || !(Number(height || 0) > 0)) {
    const dims = await readImageDimensions(file).catch(() => ({ width: 0, height: 0 }))
    width = Number(dims.width || 0) || width
    height = Number(dims.height || 0) || height
  }

  const next = [...(pendingUploadFiles.value as any[])]
  const index = next.findIndex((entry: any) => String(entry?.id || '') === String(item?.id || ''))
  if (index < 0) {
    if (previewUrl.startsWith('blob:') && previewUrl !== String(item?.previewUrl || '').trim()) {
      try { URL.revokeObjectURL(previewUrl) } catch {}
    }
    return
  }

  const prevUrl = String(next[index]?.previewUrl || '').trim()
  next[index] = {
    ...next[index],
    previewUrl,
    width,
    height,
  }
  pendingUploadFiles.value = next as any

  if (prevUrl && prevUrl !== previewUrl && prevUrl.startsWith('blob:')) {
    try { URL.revokeObjectURL(prevUrl) } catch {}
  }
}

async function onUploadBuffered(items: any[]) {
  const next = (await Promise.all(
    (Array.isArray(items) ? items : []).map((item: any) => buildOverlayPreviewFileState(item)),
  )).filter((item) => item?.file)
  if (!next.length) return
  revokePendingUploadFiles()
  pendingUploadFiles.value = next as any
  showUploadPrompt.value = false
  showPostUploadOverlay.value = true
  for (const item of next) {
    enqueuePendingUploadPreviewUpgrade(() => upgradePendingUploadPreviewFileState(item))
  }
}

async function importAdobeStockAssetToPostUpload(targetCategoryId?: number | null, targetProductId?: number | null, targetProductTitle?: string | null) {
  const rawAssetId = String(qp('assetId') || qp('stockAssetId') || '').trim()
  const categoryId = Number(targetCategoryId || selectedEntryCategoryId.value || 0)
  const productId = Number(targetProductId || selectedEntryProductId.value || 0)
  if (!rawAssetId || stockImportBusy.value || !(categoryId > 0) || !(productId > 0)) return false

  const importKey = `${rawAssetId}:${categoryId}:${productId}`
  if (handledStockImportKeys.has(importKey)) return true

  stockImportBusy.value = true
  try {
    const { file, payload } = await fetchAdobeStockPreviewFile(rawAssetId)
    const preview = await buildOverlayPreviewFileState({
      file,
      fileName: String(file.name || targetProductTitle || `adobe-stock-${rawAssetId}`),
      width: Number(payload?.width || 0) || undefined,
      height: Number(payload?.height || 0) || undefined,
    })
    if (!preview) return false
    revokePendingUploadFiles()
    pendingUploadFiles.value = [{
      ...preview,
      assetId: String(payload?.assetId || rawAssetId || '').trim() || undefined,
      stockAssetId: String(payload?.assetId || rawAssetId || '').trim() || undefined,
    }]
    showUploadPrompt.value = false
    showPostUploadOverlay.value = true
    handledStockImportKeys.add(importKey)

    const nextQuery = { ...route.query } as Record<string, any>
    delete nextQuery.assetId
    delete nextQuery.stockAssetId
    delete nextQuery.stockContinue
    await router.replace({ query: nextQuery }).catch(() => {})
    return true
  } finally {
    stockImportBusy.value = false
  }
}

function isTruthyQueryFlag(value: unknown): boolean {
  const raw = String(Array.isArray(value) ? value[0] : value ?? '').trim().toLowerCase()
  return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'open'
}

watch(
  [() => route.query.openDrawer, selectedEntryCategoryId, effectiveSelectedEntryProductId] as const,
  async ([openDrawer, categoryId, productId]) => {
    if (!isTruthyQueryFlag(openDrawer)) {
      activeOpenDrawerKey.value = null
      return
    }
    const cid = Number(categoryId || 0)
    const pid = Number(productId || 0)
    if (!(cid > 0) || !(pid > 0)) return

    const openKey = `${String(Array.isArray(openDrawer) ? openDrawer[0] : openDrawer)}:${cid}:${pid}`
    if (activeOpenDrawerKey.value === openKey) return
    activeOpenDrawerKey.value = openKey

    const handledStockImport = await importAdobeStockAssetToPostUpload(
      cid,
      pid,
      effectiveSelectedEntryProductTitle.value,
    ).catch(() => false)
    if (!handledStockImport) {
      uploadPromptSource.value = 'desktop'
      showUploadPrompt.value = true
      void buildPhoneBridge()
    }

    const nextQuery = { ...route.query } as Record<string, any>
    delete nextQuery.openDrawer
    await router.replace({ query: nextQuery }).catch(() => {})
    activeOpenDrawerKey.value = null
  },
  { immediate: true, flush: 'post' },
)

watch(
  [
    () => showUploadPrompt.value && uploadPromptSource.value === 'phone',
    showPostUploadOverlay,
    () => pendingUploadFiles.value.some((file: any) => file?.remoteUpload === true),
    selectedEntryCategoryId,
  ] as const,
  async ([phonePromptOpen, overlayOpen, hasRemoteOverlayFiles, categoryId]) => {
    const shouldWatch = !!phonePromptOpen || (!!overlayOpen && !!hasRemoteOverlayFiles)
    if (!shouldWatch || !(Number(categoryId || 0) > 0)) {
      stopPhoneUploadWatch()
      return
    }
    await startPhoneUploadWatch()
  },
  { immediate: true },
)

function onPostUploadCancel() {
  showPostUploadOverlay.value = false
  revokePendingUploadFiles()
  pendingUploadFiles.value = []
}

function requestLeaveEditor(target: string) {
  leaveConfirmTarget.value = String(target || '').trim() || '/'
}

function confirmLeaveEditor() {
  const target = String(leaveConfirmTarget.value || '').trim() || '/'
  leaveConfirmTarget.value = null
  window.location.href = target
}

async function onPostUploadApply(payload: {
  productId: number
  productTitle?: string
  categoryId: number
  categoryTitle?: string
  categoryThumbnail?: string
  sizeVariantId?: string
  sizeTitle?: string
  sizeWidthMm?: number
  sizeHeightMm?: number
  quantity?: number
  grayscale?: boolean
  options: Array<{ groupId: string; variantId: string; value: string }>
}) {
  const localFiles = [...pendingUploadFiles.value]
  if (!localFiles.length) return
  const remoteFiles = localFiles.filter((file: any) => file?.remoteUpload === true && file?.remoteItem)
  if (remoteFiles.length === localFiles.length) {
    const cfg = getWidgetConfig() as any
    const editorBaseUrl = String(cfg?.editorBaseUrl || '').trim()
    const projectCode = await project.ensureProject().catch(() => '')
    if (!editorBaseUrl || !projectCode) return

    const size =
      payload.sizeVariantId || payload.sizeTitle
        ? ({
            id: payload.sizeVariantId,
            variantId: payload.sizeVariantId,
            title: payload.sizeTitle,
            widthMm: payload.sizeWidthMm,
            heightMm: payload.sizeHeightMm,
          } as any)
        : null
    const options = Array.isArray(payload.options) ? payload.options : []
    const quantity = Math.max(1, Number(payload.quantity || 1))
    const grayscale = !!payload.grayscale
    const imageIds = remoteFiles.map((file: any) => String(file.id || '').trim()).filter(Boolean)

    await syncUploadMetaBatch({
      editorBaseUrl,
      projectCode,
      items: remoteFiles.map((file: any) => {
        const remoteItem = file.remoteItem
        const rec = mapRemoteUploadToRecordMeta(remoteItem, { projectCode })
        // Beim Produktwechsel nie den Titel des Upload-Produkts behalten (veralteter Titel im Warenkorb/XML)
        const prevProductId = Number((rec as any).productId ?? (rec as any).edits?.preset?.productId ?? 0)
        const givenTitle = String(payload.productTitle ?? '').trim()
        const nextProductTitle = givenTitle
          || (prevProductId === Number(payload.productId || 0) ? String((rec as any).edits?.preset?.productTitle ?? '').trim() : '')
          || String(catalogBootstrap.value?.products?.[String(Number(payload.productId || 0))]?.title ?? '').trim()
        const nextPreset = {
          ...(((rec as any).edits?.preset || {}) as any),
          productId: payload.productId,
          productTitle: nextProductTitle,
          ...(payload.categoryId ? { categoryId: payload.categoryId } : {}),
          ...(payload.categoryTitle ? { categoryTitle: String(payload.categoryTitle) } : {}),
          ...(size ? { sizeId: String(size.id || '').trim() || undefined, size } : {}),
          options,
          quantity,
          grayscale,
        }
        const nextCrop = buildMaxCropForRecord(
          {
            ...(rec as any),
            productId: payload.productId,
            categoryId: payload.categoryId,
            edits: {
              ...((rec as any).edits || {}),
              preset: nextPreset,
            },
          } as any,
          size,
        )

        return {
          id: rec.id,
          productId: payload.productId,
          categoryId: payload.categoryId,
          meta: {
            ...(((remoteItem as any)?.meta && typeof (remoteItem as any).meta === 'object')
              ? ((remoteItem as any).meta as Record<string, any>)
              : {}),
            productTitle: nextProductTitle,
            edits: {
              ...((((remoteItem as any)?.meta || {}) as any)?.edits || {}),
              ...(nextCrop ? { crop: nextCrop } : {}),
              lockAR: true,
              orientMode: 'auto',
              swapAR: false,
              preset: nextPreset,
            },
          },
        }
      }),
    }).catch(() => [])

    for (const id of imageIds) phoneRemoteHandledIds.add(id)
    stopPhoneUploadWatch()
    showPostUploadOverlay.value = false
    pendingUploadFiles.value = []
    await router.push({
      path: '/multi',
      query: {
        ...route.query,
        product: String(payload.productId),
        productId: String(payload.productId),
        category: String(payload.categoryId),
        categoryId: String(payload.categoryId),
        imageIds: imageIds.join(','),
        imageId: imageIds[0] || undefined,
        editorType: 'multi',
      },
    }).catch(() => {})
    return
  }

  showPostUploadOverlay.value = false
  entryUploadHandoff.set({
    files: localFiles.map((file) => ({
      id: String(file.id || crypto.randomUUID()),
      file: file.file,
      fileName: String(file.fileName || file.file?.name || 'Foto'),
      previewUrl: String(file.previewUrl || ''),
      width: Number(file.width || 0) || undefined,
      height: Number(file.height || 0) || undefined,
      assetId: String((file as any).assetId || '').trim() || undefined,
      stockAssetId: String((file as any).stockAssetId || (file as any).assetId || '').trim() || undefined,
    })),
    payload: {
      ...payload,
      options: Array.isArray(payload.options) ? payload.options : [],
      quantity: Math.max(1, Number(payload.quantity || 1)),
      grayscale: !!payload.grayscale,
    },
  })
  pendingUploadFiles.value = []

  await router.push({
    path: '/multi',
    query: {
      ...route.query,
      product: String(payload.productId),
      productId: String(payload.productId),
      category: String(payload.categoryId),
      categoryId: String(payload.categoryId),
      imageIds: localFiles.map((file) => String(file.id || '')).filter(Boolean).join(','),
      imageId: localFiles[0]?.id ? String(localFiles[0].id) : undefined,
      editorType: 'multi',
    },
  }).catch(() => {})
}

onBeforeUnmount(() => {
  stopPhoneUploadWatch()
})

/* ---------- Actions ---------- */
async function selectParent(c: CategoryVm) {
  const printboxFamilyId = resolvePrintboxFamilyId(c)
  if (printboxFamilyId) {
    window.location.href = buildPrintboxEditorUrl(printboxFamilyId)
    return
  }

  parentCat.value = c
  childCat.value = null
  step.value = 'children'

  await setQuery({
    parent: String(c._id),
  })
  await clearQuery(['category', 'categoryId', 'categoryIri', 'categoryName', 'parentName', 'editorType', 'product', 'productId', 'productIri', 'productName', 'pool', 'openDrawer', 'imageIds'])

  await refreshChildren()
  const visibleChildren = visibleChildrenFor(c._id)

  if (visibleChildren.length === 0) {
    childCat.value = c
    step.value = 'products'
    await setQuery({
      category: String(c._id),
      categoryId: String(c._id),
      editorType: c.editorType ?? null,
    })
    await refreshProducts()
  }
}

async function selectChild(c: CategoryVm) {
  childCat.value = c
  step.value = 'products'

  await setQuery({
    category: String(c._id),
    categoryId: String(c._id),
    editorType: c.editorType ?? null,
  })
  await clearQuery(['product', 'productId', 'productIri', 'productName', 'categoryIri', 'categoryName', 'pool', 'openDrawer', 'imageIds'])

  emit('category:selected', { categoryId: c._id, title: c.title })
  await refreshProducts()
}

async function selectProduct(p: ProductVm) {
  await setQuery({
    product: String(p._id),
  })

  emit('product:selected', { productId: p._id, title: p.title })

  if (props.autoNavigateToEditor) {
    await router.push({
      path: '/' + resolvedEditorType.value,
      query: {
        ...route.query,
        product: String(p._id),
        ...(resolvedChildId.value ? { category: String(resolvedChildId.value), categoryId: String(resolvedChildId.value) } : {}),
      },
    })
  } else {
    selectedEntryProductId.value = p._id
    selectedEntryProductTitle.value = p.title
    const handledStockImport = await importAdobeStockAssetToPostUpload(
      childCat.value?._id ?? resolvedChildId.value ?? parentCat.value?._id ?? null,
      p._id,
      p.title,
    ).catch(() => false)
    if (handledStockImport) return
    uploadPromptSource.value = 'desktop'
    showUploadPrompt.value = true
    void buildPhoneBridge()
  }
}

async function goToParents() {
  parentCat.value = null
  childCat.value = null
  step.value = 'parents'
  await clearQuery([
    'parent',
    'parentName',
    'category',
    'categoryId',
    'categoryIri',
    'categoryName',
    'editorType',
    'product',
    'productId',
    'productIri',
    'productName',
  ])
  await refreshParents()
}

async function goToChildren() {
  step.value = 'children'
  await clearQuery(['product', 'productId', 'productIri', 'productName'])
  await refreshChildren()
  const visibleChildren = visibleChildrenFor(parentCat.value?._id ?? resolvedParentId.value ?? 0)
  if (visibleChildren.length === 0) await goToParents()
}

async function resetAll() {
  await goToParents()
}
</script>

<style scoped>
.entry {
  padding: 24px 24px 30px;
  background: #fff;
  color: #0f172a;
  min-height: 100%;
}

.entry-logo-wrap {
  display: flex;
  justify-content: center;
  padding: 0 0 18px;
}

.entry-logo-link {
  display: inline-flex;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.topbar {
  display: grid;
  gap: 10px;
  padding: 0 0 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.09);
}

.hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
}

.entry-logo {
  display: block;
  height: 44px;
  width: auto;
  max-width: min(380px, 72vw);
  object-fit: contain;
}

.eyebrow {
  font-size: 11px;
  line-height: 1;
  text-transform: uppercase;
  color: rgba(15, 23, 42, 0.48);
  font-weight: 700;
}

.headline {
  margin: 0;
  font-size: clamp(28px, 3vw, 42px);
  line-height: 0.96;
  font-weight: 700;
}
.btn.btn-editor-link{
  flex: 0 0 auto;
  min-height: 40px;
  padding: 0 16px;
  background: #e30b17;
  border-color: #e30b17;
  color: #fff;
  font-weight: 700;
}
.btn-editor-link:hover{
  background: #c80913;
  border-color: #c80913;
}

.crumbs {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.6);
}
.sep {
  color: rgba(15, 23, 42, 0.28);
}
.crumb-btn,
.crumb-current {
  display: inline-flex;
  align-items: center;
}
.crumb-btn {
  border: none;
  cursor: pointer;
  color: rgba(15, 23, 42, 0.78);
  background: transparent;
  padding: 0;
  transition: color .14s ease;
}
.crumb-btn:hover {
  color: #0f172a;
  text-decoration: underline;
}
.crumb-current {
  color: #0f172a;
  font-weight: 600;
  padding: 0;
}
.btn {
  border: 1px solid rgba(15, 23, 42, 0.14);
  background: #fff;
  min-height: 38px;
  padding: 0 14px;
  cursor: pointer;
  font-size: 13px;
  border-radius: 2px;
}
.btn.ghost {
  background: rgba(15, 23, 42, 0.04);
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.panel {
  margin-top: 20px;
}

.stack {
  display: grid;
  gap: 26px;
}

.section-block {
  display: grid;
  gap: 12px;
}

.section-block + .section-block {
  padding-top: 24px;
  border-top: 1px solid rgba(15, 23, 42, 0.09);
}

.section-title {
  margin: 0;
  font-size: clamp(28px, 3vw, 42px);
  line-height: 0.96;
  font-weight: 700;
  color: #0f172a;
}

.panel-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}
.panel-head h2 {
  margin: 0;
  font-size: 14px;
  text-transform: uppercase;
  color: rgba(15, 23, 42, 0.54);
}
.spacer {
  flex: 1;
}

.grid {
  padding: 18px 0 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
  align-items: start;
}

.card {
  border: none;
  background: #fff;
  cursor: pointer;
  text-align: left;
  border-radius: 0;
  padding: 0;
  display: grid;
  align-content: start;
  transition: transform 120ms ease, opacity 120ms ease;
}
.card:hover {
  transform: translateY(-2px);
}
.card--category .thumb {
  aspect-ratio: 4 / 3;
}

.thumb {
  width: 100%;
  aspect-ratio: 16 / 11;
  object-fit: cover;
  display: block;
  background: rgba(15, 23, 42, 0.04);
  border-radius: 0;
}

.meta {
  padding: 12px 0 2px;
  display: grid;
  gap: 5px;
  align-content: start;
}
.name {
  font-size: 18px;
  line-height: 1.1;
  font-weight: 600;
}
.entry-sub {
  font-size: 13px;
  line-height: 1.35;
  font-weight: 400;
}
.muted {
  color: rgba(15, 23, 42, 0.6);
}
.empty {
  padding: 14px 0;
}
.error {
  padding: 14px 0;
  color: #b91c1c;
}

.entry-upload-hidden {
  display: none;
}

.uprompt-backdrop{
  position: fixed;
  inset: 0;
  z-index: 1700;
  background: rgba(15,23,42,.42);
  display: grid;
  place-items: center;
  padding: 16px;
}
.uprompt-panel{
  width: min(420px, calc(100vw - 32px));
  background: #fff;
  border: 1px solid rgba(15,23,42,.10);
  box-shadow: 0 22px 70px rgba(2,6,23,.22);
  border-radius: 4px;
  padding: 18px;
  display: grid;
  gap: 8px;
}
.uprompt-panel--status,
.uprompt-panel--confirm{
  width: min(460px, calc(100vw - 32px));
}
.uprompt-title{
  font-size: 16px;
  font-weight: 700;
  color: #141414;
}
.uprompt-text{
  font-size: 14px;
  color: rgba(15,23,42,.64);
}
.uprompt-panel:has(.uprompt-modes){
  width: min(480px, calc(100vw - 32px));
}
.uprompt-modes{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
  margin:4px 0 6px;
}
.uprompt-mode{
  position:relative;
  display:flex;
  align-items:flex-start;
  gap:10px;
  padding:12px;
  text-align:left;
  border:2px solid rgba(15,23,42,.14);
  border-radius:6px;
  background:#fff;
  color:#18212f;
  cursor:pointer;
  transition:border-color .15s ease, box-shadow .15s ease, transform .15s ease;
}
.uprompt-mode[data-on='1']{
  border-color:#e30b17;
  background:#fff7f7;
  cursor:default;
}
.uprompt-mode[data-on='0']:hover{
  border-color:rgba(15,23,42,.32);
  box-shadow:0 6px 18px rgba(2,6,23,.08);
  transform:translateY(-1px);
}
.uprompt-mode:disabled{opacity:.6;cursor:progress}
.uprompt-mode__icon{
  flex:0 0 34px;
  height:34px;
  border-radius:50%;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  background:rgba(227,11,23,.10);
  color:#e30b17;
}
.uprompt-mode__icon--design{
  background:linear-gradient(135deg, rgba(124,58,237,.14), rgba(227,11,23,.12));
  color:#7c3aed;
}
.uprompt-mode__copy{display:grid;gap:2px;min-width:0;padding-right:14px}
.uprompt-mode__title{font-size:13px;font-weight:700;line-height:1.25}
.uprompt-mode__sub{font-size:12px;line-height:1.35;color:rgba(15,23,42,.6)}
.uprompt-mode__go{position:absolute;right:10px;top:12px;color:rgba(15,23,42,.45)}
@media (max-width:480px){
  .uprompt-modes{grid-template-columns:1fr}
}
.uprompt-switch{
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:8px;
  margin-top: 2px;
}
.uprompt-source{
  min-height:34px;
  border:2px solid rgba(15,23,42,.16);
  border-radius:4px;
  background:#fff;
  color:#18212f;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:7px;
  font-size:12px;
  font-weight:700;
}
.uprompt-source[data-on='1']{
  color: #e30b17;
  border-color: #e30b17;
}
.uprompt-dropzone{
  border:2px dashed rgba(15,23,42,.18);
  border-radius:4px;
  background:#fafafa;
  display:grid;
  place-items:center;
  gap:4px;
  padding:18px 14px;
  text-align:center;
  cursor:pointer;
  transition:border-color .15s ease, background .15s ease;
}
.uprompt-dropzone:hover,
.uprompt-dropzone--drag{
  border-color:rgba(15,23,42,.28);
  background:#f8f8f8;
}
.uprompt-dropzone__icon{
  color:#e30b17;
}
.uprompt-dropzone__title{
  font-size:14px;
  font-weight:700;
  color:#141414;
}
.uprompt-dropzone__sub{
  font-size:12px;
  color:rgba(15,23,42,.62);
}
.uprompt-dropzone__hint{
  font-size:11px;
  color:rgba(15,23,42,.42);
}
.uprompt-phone{
  display:grid;
  grid-template-columns:108px minmax(0,1fr);
  gap:10px;
  align-items:center;
  margin-top: 2px;
}
.uprompt-phone-qr{
  min-height:108px;
  border:1px solid rgba(15,23,42,.12);
  border-radius:8px;
  background:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
}
.uprompt-phone-qr img{
  width:92px;
  height:92px;
  display:block;
}
.uprompt-phone-qrph{
  padding:12px;
  text-align:center;
  font-size:12px;
  color: rgba(15,23,42,.56);
}
.uprompt-phone-head{
  display:flex;
  gap:8px;
  align-items:flex-start;
}
.uprompt-phone-title{
  font-size:14px;
  font-weight:700;
  color:#141414;
}
.uprompt-phone-sub{
  margin-top:2px;
  font-size:12px;
  line-height:1.4;
  color: rgba(15,23,42,.64);
}
.uprompt-phone-ok{
  margin-top:8px;
  font-size:12px;
  font-weight:700;
  color:#166534;
}
.uprompt-phone-actions{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin-top:10px;
}
.uprompt-actions{
  display:flex;
  justify-content:flex-end;
  gap:8px;
  margin: 2px -18px -18px;
  padding: 12px 18px;
  border-top:1px solid rgba(15,23,42,.08);
}
.uprompt-progress{
  height: 8px;
  background: rgba(15,23,42,.08);
  border-radius: 999px;
  overflow: hidden;
  margin-top: 8px;
}
.uprompt-progress__bar{
  display: block;
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #e30b17 0%, #ff7b5a 100%);
  animation: uprompt-progress-slide 1.15s ease-in-out infinite;
}
@keyframes uprompt-progress-slide{
  0%{ transform: translateX(-120%); }
  100%{ transform: translateX(300%); }
}

/* modern skeleton */
.skel .sk-card {
  background: #fff;
  overflow: hidden;
}
.sk-img {
  aspect-ratio: 16 / 10;
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.04), rgba(15, 23, 42, 0.09), rgba(15, 23, 42, 0.04));
  background-size: 220% 100%;
  animation: shimmer 1.1s linear infinite;
}
.sk-line {
  height: 10px;
  margin: 10px;
  background: linear-gradient(90deg, rgba(15, 23, 42, 0.04), rgba(15, 23, 42, 0.09), rgba(15, 23, 42, 0.04));
  background-size: 220% 100%;
  animation: shimmer 1.1s linear infinite;
}
.sk-line.l1 {
  width: 70%;
}
.sk-line.l2 {
  width: 45%;
  margin-top: 0;
  margin-bottom: 14px;
}

@media (max-width: 767px) {
  .entry {
    padding: 18px 16px 24px;
  }
  .entry-logo-wrap {
    justify-content: flex-start;
    padding-bottom: 14px;
  }
  .topbar {
    grid-template-columns: 1fr;
  }
  .hero {
    display: grid;
    justify-items: start;
    text-align: left;
  }
  .headline {
    font-size: 28px;
  }
  .btn-editor-link{
    min-height: 38px;
  }
  .entry-logo {
    height: 28px;
    max-width: 240px;
  }
  .grid {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .name {
    font-size: 16px;
  }
  .uprompt-phone{
    grid-template-columns:1fr;
  }
}

@keyframes shimmer {
  0% {
    background-position: 0% 0;
  }
  100% {
    background-position: 220% 0;
  }
}
</style>
